import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PanelHelp } from "./QuickStart";
import { toast } from "sonner";
import { Download, Loader2, Target } from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";
import {
  buildOverlayMatrix,
  OVERLAY_THRESHOLDS,
  EXACTA_DEFAULT_BASE,
} from "../lib/exactaOverlays";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { lowestDdpoPost } from "../lib/excelHelpers";
import { OverlayTicketBuilder } from "./OverlayTicketBuilder";

const KEY_ON_STORAGE = "raceHandicap.exactaKeyOn.v1";

const monoStyle = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

export const ExactaOverlays = ({
  apiBase,
  trackSlug,
  trackName,
  race,
  leftRows,
  leftElims,
  keyPost,
  onToggleKey,
  date,
}) => {
  const open = true;
  const [threshold, setThreshold] = useState(30);
  const [ex, setEx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [base, setBase] = useState(EXACTA_DEFAULT_BASE);
  const [keyOn, setKeyOn] = useState(
    () => localStorage.getItem(KEY_ON_STORAGE) !== "0"
  );
  const toggleKeyOn = () =>
    setKeyOn((v) => {
      localStorage.setItem(KEY_ON_STORAGE, v ? "0" : "1");
      return !v;
    });
  const toggleKey = onToggleKey;

  const capture = useCallback(
    async ({ silent = false } = {}) => {
      if (!trackSlug) {
        if (!silent) toast.error("Select a track in the loader above first.");
        return;
      }
      setBusy(true);
      try {
        const exResp = await fetch(
          `${apiBase}/fetch-exacta-probables?track=${encodeURIComponent(trackSlug)}`
        );
        if (!exResp.ok) throw new Error(`Exacta: HTTP ${exResp.status}`);
        const exData = await exResp.json();
        setEx(exData);
        setLastRefresh(Date.now());
        if (silent) return;
        const n = Object.keys(exData.matrix || {}).length;
        if (n === 0) {
          toast.warning(
            exData.note || "Pool not open yet — try again closer to post."
          );
        } else {
          toast.success(
            `Captured R${exData.race ?? "?"} — ${n} entries · pool $${
              exData.pool ? Number(exData.pool).toLocaleString() : "—"
            }`
          );
        }
      } catch (err) {
        if (!silent) toast.error(`Capture failed: ${err.message || err}`);
      } finally {
        setBusy(false);
      }
    },
    [apiBase, trackSlug]
  );

  // Auto-capture once per real track/race change (refresh blips where the
  // race briefly flips type or goes empty must not wipe the grid/ticket).
  const capturedFor = React.useRef(null);
  useEffect(() => {
    if (!trackSlug || race == null || race === "") return;
    const k = `${trackSlug}|${String(race)}`;
    if (capturedFor.current === k) return;
    capturedFor.current = k;
    setEx(null);
    capture({ silent: true });
  }, [trackSlug, race, capture]);

  // Silent 30s refresh. Disabled automatically when the panel is
  // collapsed or no track is selected, and pauses when the tab is
  // hidden (via useAutoRefresh).
  const refreshTick = useCallback(() => {
    capture({ silent: true });
  }, [capture]);
  useAutoRefresh(refreshTick, {
    enabled: auto && open && !!trackSlug,
    intervalMs: 30000,
  });

  const { ranked, cells } = useMemo(
    () =>
      buildOverlayMatrix({
        leftRows,
        odds: ex?.odds || {},
        exactaMatrix: ex?.matrix || {},
        thresholdPct: threshold,
        base,
        publishedBase: ex?.published_base || 1,
      }),
    [leftRows, ex, threshold, base]
  );
  const haveData = ranked.length > 0 && !!ex;
  const overlayCount = cells.filter((c) => c.isOverlay).length;

  // The effective key is the user-locked post if set; otherwise the
  // lowest-DDPO horse (falls back to the first entry in `ranked`). A null `effectiveKey`
  // (no DDPO ranks yet) means we don't dim anything.
  const lowDdpo = useMemo(() => lowestDdpoPost(leftRows || [], leftElims), [leftRows, leftElims]);
  const topDdpo = ranked.includes(lowDdpo) ? lowDdpo : ranked[0] ?? null;
  const effectiveKey = keyPost ?? topDdpo;
  const isUserLocked = keyPost != null;

  // Build a quick lookup for cell rendering.
  const cellByKey = useMemo(() => {
    const m = {};
    for (const c of cells) m[`${c.firstPost}-${c.secondPost}`] = c;
    return m;
  }, [cells]);

  return (
    <section
      className="rounded-lg border border-stone-800 bg-[#0e0e0e] mt-5 overflow-hidden"
      data-testid="exacta-overlays"
    >
      <div
        className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-stone-800"
        data-testid="exacta-overlays-header"
      >
        <div className="flex items-center gap-2 text-[#f5ecd6]">
          <Target size={16} className="text-[#84cc16]" />
          <span className="text-sm uppercase tracking-[0.2em]" style={monoStyle}>
            Exacta Overlays
          </span>
          <PanelHelp step={6} label="Exacta Overlays" />
          {trackName && (
            <span
              className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#1a2e05] text-[#a3e635] border border-[#365314] font-bold tracking-[0.14em]"
              style={monoStyle}
              data-testid="exacta-overlays-target"
            >
              {trackName}{race ? ` · R${race}` : ""}
            </span>
          )}
          {overlayCount > 0 && (
            <span
              className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#84cc16] text-[#0a0a0a] font-bold tracking-[0.14em]"
              style={monoStyle}
            >
              {overlayCount} overlays
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="p-4 space-y-4">
          <p
            className="text-[11px] text-stone-400 leading-5"
            style={monoStyle}
          >
            Actual = live Cybertote Exacta probables, converted from the
            track's quoted exacta base to your base. Fair = Win(1st) × (Win(2nd) + 1) × base, using the
            win odds from the same probables page. Overlay % = (Actual − Fair)
            ÷ Fair. Rows and columns are reordered by IQ Power rank; overlays are
            numbered in anti-diagonal order.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => capture()}
              disabled={!trackSlug || busy}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded border border-[#365314] bg-[#1a2e0a] text-[#a3e635] text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-[#84cc16] hover:text-[#0a0a0a] disabled:opacity-40"
              style={monoStyle}
              data-testid="exacta-capture-btn"
            >
              {busy ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Download size={12} />
              )}
              Capture Now
            </button>
            <label
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-stone-300 cursor-pointer select-none"
              style={monoStyle}
              data-testid="exacta-auto-toggle"
              title={
                !trackSlug
                  ? "Select a track above first"
                  : "Re-captures Exacta probables every 30 seconds. Pauses while this tab is hidden."
              }
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                disabled={!trackSlug}
                className="accent-[#84cc16] w-4 h-4 disabled:opacity-40"
              />
              Auto-refresh 30s
              {auto && (
                <span className="inline-flex h-2 w-2 rounded-full bg-[#84cc16] animate-pulse" />
              )}
            </label>
            {lastRefresh && (
              <span
                className="text-[10px] uppercase tracking-[0.2em] text-stone-500"
                style={monoStyle}
                data-testid="exacta-last-refresh"
              >
                Updated{" "}
                {new Date(lastRefresh).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
            <div className="flex items-center gap-2" style={monoStyle}>
              <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">
                Overlay ≥
              </span>
              {OVERLAY_THRESHOLDS.map((t) => {
                const active = t === threshold;
                return (
                  <button
                    key={t}
                    onClick={() => setThreshold(t)}
                    data-testid={`exacta-thresh-${t}`}
                    className={`px-2 h-7 text-[10px] font-bold tracking-[0.14em] uppercase rounded border transition ${
                      active
                        ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                        : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
                    }`}
                  >
                    {t}%
                  </button>
                );
              })}
            </div>
            {ex?.pool && (
              <span
                className="text-[10px] uppercase tracking-[0.2em] text-stone-500"
                style={monoStyle}
              >
                Pool ${Number(ex.pool).toLocaleString()}
              </span>
            )}
            <div className="flex items-center gap-1.5" style={monoStyle}>
              <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">
                Base
              </span>
              {[1, 2].map((b) => (
                <button
                  key={b}
                  onClick={() => setBase(b)}
                  data-testid={`exacta-base-${b}`}
                  className={`px-2 h-7 text-[10px] font-bold tracking-[0.14em] rounded border transition-colors ${
                    base === b
                      ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                      : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
                  }`}
                  title={`Show Actual and Fair on a $${b} bet`}
                >
                  ${b}
                </button>
              ))}
            </div>
            {ex?.race != null && (
              <span
                className="text-[10px] uppercase tracking-[0.2em] text-stone-500"
                style={monoStyle}
                data-testid="exacta-race"
              >
                Probables R{ex.race} · quoted ${ex.published_base || 1}
                {ex.published_base_source ? ` (${ex.published_base_source})` : ""}
              </span>
            )}
            {effectiveKey != null && (
              <button
                type="button"
                onClick={toggleKeyOn}
                data-testid="exacta-key-onoff"
                aria-pressed={keyOn}
                className={`px-2 h-7 text-[10px] font-bold tracking-[0.14em] uppercase rounded border transition-colors ${
                  keyOn
                    ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                    : "bg-transparent text-stone-400 border-stone-700 hover:border-stone-500"
                }`}
                style={monoStyle}
                title="Key focus on this panel only — Off shows the full matrix. Your locked key is unchanged elsewhere."
              >
                Key: {keyOn ? "On" : "Off"}
              </button>
            )}
            {effectiveKey != null && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 h-7 rounded border transition-opacity ${keyOn ? "" : "opacity-40"} ${
                  isUserLocked
                    ? "bg-[#1a2e05] text-[#a3e635] border-[#84cc16]"
                    : "text-stone-400 border-stone-700"
                }`}
                style={monoStyle}
                data-testid="exacta-key-badge"
                title={
                  isUserLocked
                    ? "Key horse locked — click the Clear button or click the same saddle in the matrix to release."
                    : "Default key = lowest DDPO (most bet-down). Click any saddle in the matrix to lock a different key."
                }
              >
                KEY · #{effectiveKey}
                {isUserLocked && (
                  <button
                    type="button"
                    onClick={() => toggleKey?.(keyPost)}
                    data-testid="exacta-key-clear"
                    className="ml-1 text-[#fca5a5] hover:text-white"
                    title="Clear user-locked key — revert to the lowest-DDPO horse"
                  >
                    ×
                  </button>
                )}
              </span>
            )}
          </div>

          {ranked.length === 0 && (
            <div
              className="text-[11px] text-stone-500 italic px-2"
              style={monoStyle}
              data-testid="exacta-overlays-empty"
            >
              Waiting for IQ Power ranks — the grid fills in automatically once
              the DD Matrix is ranked.
            </div>
          )}

          {haveData && ranked.length > 0 && (
            <OverlayGrid
              ranked={ranked}
              cellByKey={cellByKey}
              keyPost={keyOn ? effectiveKey : null}
              userLocked={isUserLocked}
              onPickKey={toggleKey}
            />
          )}
          {trackSlug && (
            <OverlayTicketBuilder
              cells={cells}
              overlayCount={overlayCount}
              trackName={trackName}
              trackSlug={trackSlug}
              race={race}
              date={date}
            />
          )}
        </div>
      )}
    </section>
  );
};

const fmtMoney = (v) => (v >= 100 ? v.toFixed(0) : v.toFixed(1));

const colorForOverlay = (pct) => {
  if (pct == null) return null;
  if (pct >= 40) return "#84cc16"; // lime
  if (pct >= 30) return "#a3e635";
  if (pct >= 20) return "#fde047"; // yellow
  if (pct >= 10) return "#fbbf24"; // amber
  return null;
};

const OverlayGrid = ({ ranked, cellByKey, keyPost, userLocked, onPickKey }) => {
  const n = ranked.length;
  // Shared classes for a clickable header saddle. Lime ring when the
  // post is the current key (lock); dashed grey hint when it isn't.
  const headerBtn = (post) => {
    const active = post === keyPost;
    return {
      type: "button",
      onClick: () => onPickKey?.(post),
      title: active
        ? userLocked
          ? `#${post} is the locked key — click to release`
          : `#${post} is the default key (lowest DDPO) — click a different saddle to lock in a different key`
        : `Click to lock #${post} as the Exacta key`,
      "data-testid": `exacta-key-pick-${post}`,
      className: `rounded transition ${
        active
          ? "ring-2 ring-[#84cc16] bg-[#1a2e05]"
          : "hover:bg-[#1a1a1a] hover:ring-1 hover:ring-stone-600"
      }`,
      style: { padding: 2 },
    };
  };
  return (
    <div className="overflow-x-auto" data-testid="exacta-overlay-grid">
      <table
        className="border-collapse"
        style={{ ...monoStyle, borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th
              className="p-1 text-[9px] uppercase tracking-[0.2em] text-stone-500"
              style={monoStyle}
            >
              2nd ↓ · 1st →
            </th>
            {ranked.map((p) => (
              <th
                key={`h-${p}`}
                className="p-1 bg-[#0a0a0a] border border-stone-800"
              >
                <button {...headerBtn(p)}>
                  <SaddleCloth n={p} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ranked.map((rowPost, j) => (
            <tr key={`r-${rowPost}`}>
              <th className="p-1 bg-[#0a0a0a] border border-stone-800">
                <button {...headerBtn(rowPost)}>
                  <SaddleCloth n={rowPost} />
                </button>
              </th>
              {ranked.map((colPost, i) => {
                const key = `${colPost}-${rowPost}`;
                const c = cellByKey[key];
                const same = i === j;
                const bg = c?.isOverlay ? colorForOverlay(c.overlayPct) : null;
                // Dim any cell that doesn't touch the key horse's row
                // or column, so the user can focus on the key's combos.
                const touchesKey =
                  keyPost != null &&
                  (rowPost === keyPost || colPost === keyPost);
                const dimmed = keyPost != null && !touchesKey && !same;
                return (
                  <td
                    key={`c-${colPost}-${rowPost}`}
                    data-testid={`exacta-cell-${colPost}-${rowPost}`}
                    className="relative p-1 border border-stone-800 text-center align-middle"
                    style={{
                      minWidth: 96,
                      height: 68,
                      background: same ? "#1c1917" : bg || "#0a0a0a",
                      color: bg ? "#0a0a0a" : "#f5ecd6",
                      opacity: dimmed ? 0.25 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {same ? (
                      <span className="text-stone-700">—</span>
                    ) : c?.actual == null ? (
                      <span className="text-stone-700 text-xs">–</span>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-0.5">
                        {c.rank != null && (
                          <span
                            className="absolute top-1 right-1 inline-flex items-center justify-center rounded-full font-bold text-[10px]"
                            style={{
                              width: 18,
                              height: 18,
                              background: "#0a0a0a",
                              color: "#84cc16",
                              border: "2px solid #84cc16",
                            }}
                          >
                            {c.rank}
                          </span>
                        )}
                        <span
                          className="text-[13px] font-bold leading-none"
                          style={{ color: bg ? "#0a0a0a" : "#f5ecd6" }}
                        >
                          ${fmtMoney(c.actual)}
                        </span>
                        {c.implied != null && (
                          <span
                            className="text-[10px] leading-none"
                            style={{
                              color: bg ? "#1c1917" : "#78716c",
                            }}
                          >
                            fair ${fmtMoney(c.implied)}
                          </span>
                        )}
                        {c.overlayPct != null && (
                          <span
                            className="text-[9px] font-bold leading-none"
                            style={{
                              color: bg
                                ? "#0a0a0a"
                                : c.overlayPct > 0
                                ? "#84cc16"
                                : "#f87171",
                            }}
                          >
                            {c.overlayPct > 0 ? "+" : ""}
                            {c.overlayPct.toFixed(0)}%
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className="pt-3 text-[10px] uppercase tracking-[0.18em] text-stone-500"
        style={monoStyle}
      >
        Columns = 1st-place IQ Power rank · Rows = 2nd-place IQ Power rank · Ranks walk
        anti-diagonals (both horses highly ranked = low rank number) ·
        <span className="text-stone-400"> Click any saddle to lock a Key horse</span>
      </div>
    </div>
  );
};
